/**
 * Strategy Yes/No — lives on Management, not Budget Pacing.
 *
 * Analyze still uses the existing reallocation service. Yes approves and
 * applies the package. No rejects it. This does not change Save split or
 * the Mon/Thu pacing job.
 *
 * Slice A: Review opens a view-only modal of analyze-window facts, the
 * pull timestamp, and Now → Proposed → uncapped want. No writes. The
 * audit-log row appears only after Yes.
 *
 * Slice B: after Yes, Verify re-reads Ads dailies. Undo is package-level
 * back to Now. It does not touch Save split.
 */
import { useState } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  Check,
  Loader2,
  Scale,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import {
  analyzeBudgetReallocation,
  applyBudgetReallocation,
  undoBudgetReallocation,
  verifyBudgetReallocation,
  type BudgetAnalyzeResponse,
  type BudgetProposal,
  type BudgetVerifyRow,
} from '@/services/googleAdsMonthlyService';
import { resolveApproval } from '@/services/googleAdsWeeklyService';
import { isPreviousCalendarMonth } from '@/components/google-ads/budgetStrategyWindow';

function formatDaily(micros: number): string {
  return (micros / 1_000_000).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

function formatPulledAt(iso: string | null | undefined): string {
  if (!iso) return 'unknown pull time';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return `${parsed.toISOString().replace('T', ' ').replace(/\.\d+Z$/, '')} UTC`;
}

function deltaPct(current: number, other: number): number {
  if (current <= 0) return 0;
  return ((other - current) / current) * 100;
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="tabular-nums">{value}</div>
    </div>
  );
}

function BudgetStrategyReviewModal({
  proposal,
  periodStart,
  periodEnd,
  analyzedAt,
  onClose,
}: {
  proposal: BudgetProposal | null;
  periodStart: string;
  periodEnd: string;
  analyzedAt?: string | null;
  onClose: () => void;
}) {
  const current = proposal?.current_amount_micros ?? 0;
  const proposed = proposal?.proposed_amount_micros ?? 0;
  const uncapped = proposal?.uncapped_want_amount_micros ?? proposed;
  const clamped = Boolean(proposal?.was_budget_clamped);
  const conversions = proposal?.conversions ?? 0;
  const cpaMicros = proposal?.cost_per_conversion_micros ?? 0;

  return (
    <Dialog open={!!proposal} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{proposal?.campaign_name ?? 'Campaign'}</DialogTitle>
          <DialogDescription>
            Analyze window {periodStart} → {periodEnd}. Data pulled{' '}
            {formatPulledAt(analyzedAt)}. These are window facts, not live
            Ads numbers.
          </DialogDescription>
        </DialogHeader>

        {proposal ? (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-3 gap-2 rounded-md border border-border bg-warm-bg p-3">
              <Fact label="Now" value={formatDaily(current)} />
              <Fact label="Proposed" value={formatDaily(proposed)} />
              <Fact label="Uncapped want" value={formatDaily(uncapped)} />
            </div>
            <p className="text-xs text-muted-foreground">
              Proposed vs now:{' '}
              <span className="tabular-nums">
                {deltaPct(current, proposed) >= 0 ? '+' : ''}
                {deltaPct(current, proposed).toFixed(1)}%
              </span>
              . Uncapped want vs now:{' '}
              <span className="tabular-nums">
                {deltaPct(current, uncapped) >= 0 ? '+' : ''}
                {deltaPct(current, uncapped).toFixed(1)}%
              </span>
              .
              {clamped
                ? ' Proposed is Art\'s ±15% relearn cap. The exact ±15% is the clamp, not a coincidence.'
                : ' Cap did not bind — proposed equals uncapped want.'}
            </p>

            <div className="grid grid-cols-2 gap-2 rounded-md border border-border bg-warm-bg p-3 sm:grid-cols-4">
              <Fact
                label="Spend (window)"
                value={formatDaily(proposal.spend_micros ?? 0)}
              />
              <Fact
                label="Clicks"
                value={formatCount(proposal.clicks ?? 0)}
              />
              <Fact
                label="Conversions"
                value={formatCount(conversions)}
              />
              <Fact
                label="CPA"
                value={
                  conversions > 0 && cpaMicros > 0
                    ? formatDaily(cpaMicros)
                    : '—'
                }
              />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Why
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{proposal.reason}</p>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function verifyLabel(status: string | undefined): string | null {
  if (status === 'applied') return 'Applied';
  if (status === 'mismatch') return 'Mismatch';
  if (status === 'unavailable') return 'Unavailable';
  return null;
}

function ProposalRow({
  proposal,
  onReview,
  factsLive,
  verifyStatus,
}: {
  proposal: BudgetProposal;
  onReview: (proposal: BudgetProposal) => void;
  factsLive: boolean;
  verifyStatus?: string;
}) {
  const up = proposal.delta_micros > 0;
  const movePct = deltaPct(proposal.current_amount_micros, proposal.proposed_amount_micros);
  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{proposal.campaign_name}</div>
        <div className="text-[11px] text-muted-foreground">{proposal.campaign_id}</div>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatDaily(proposal.current_amount_micros)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatDaily(proposal.proposed_amount_micros)}
      </TableCell>
      <TableCell
        className={`text-right tabular-nums ${
          up ? 'text-[hsl(var(--status-success))]' : 'text-destructive'
        }`}
      >
        {up ? (
          <ArrowUpRight className="mr-1 inline h-3 w-3" />
        ) : (
          <ArrowDownRight className="mr-1 inline h-3 w-3" />
        )}
        {formatDaily(proposal.delta_micros)}
        <div className="text-[10px] text-muted-foreground">
          ({movePct >= 0 ? '+' : ''}
          {movePct.toFixed(1)}%)
        </div>
      </TableCell>
      <TableCell className="max-w-[28rem] text-xs text-muted-foreground">
        {proposal.reason}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          {verifyLabel(verifyStatus) ? (
            <Badge
              variant="outline"
              className={
                verifyStatus === 'applied'
                  ? ''
                  : 'border-destructive/40 text-destructive'
              }
            >
              {verifyLabel(verifyStatus)}
            </Badge>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-label={`Review ${proposal.campaign_name}`}
            disabled={!factsLive}
            onClick={() => {
              if (!factsLive) return;
              onReview(proposal);
            }}
          >
            Review
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function BudgetStrategyPanel({
  tenantId,
  embedded = false,
}: {
  tenantId: string;
  embedded?: boolean;
}) {
  const { user } = useAuth();
  const operatorEmail = user?.email ?? 'ui-operator';
  const [result, setResult] = useState<BudgetAnalyzeResponse | null>(null);
  const [reviewing, setReviewing] = useState<BudgetProposal | null>(null);
  const [applied, setApplied] = useState(false);
  const [undone, setUndone] = useState(false);
  const [verifyRows, setVerifyRows] = useState<Record<string, BudgetVerifyRow>>(
    {},
  );

  const analyze = useMutation({
    mutationFn: () =>
      analyzeBudgetReallocation({
        tenantId,
        mode: 'soft_auto',
        budgetAdjustmentRule: 'art_relearn_cap',
      }),
    onSuccess: (next) => {
      setApplied(false);
      setUndone(false);
      setVerifyRows({});
      setResult(next);
      if (next.skipped_reason) {
        toast.info(`Skipped: ${next.skipped_reason}`);
        return;
      }
      if (next.proposals.length === 0) {
        toast.info('No strategy move. The review held conservative.');
        return;
      }
      toast.success(
        `${next.proposals.length} move${next.proposals.length === 1 ? '' : 's'} ready for Yes or No`,
      );
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Strategy review failed: ${detail}`);
    },
  });

  const decide = useMutation({
    mutationFn: async (action: 'approve' | 'reject') => {
      if (!result?.approval_id) {
        throw new Error('Nothing to resolve');
      }
      await resolveApproval({
        requestId: result.approval_id,
        action,
        tenantId,
        resolvedBy: operatorEmail,
      });
      if (action === 'approve') {
        return applyBudgetReallocation(result.approval_id);
      }
      return null;
    },
    onSuccess: (applied, action) => {
      if (action === 'reject') {
        toast.success('No — left Ads and Save split unchanged');
        setResult(null);
        setReviewing(null);
        setApplied(false);
        setUndone(false);
        setVerifyRows({});
        return;
      }
      toast.success(
        `Yes — wrote ${applied?.applied ?? 0} Google Ads daily${
          (applied?.applied ?? 0) === 1 ? '' : 's'
        }. Save split was not updated.`,
      );
      setApplied(true);
      setUndone(false);
      setVerifyRows({});
      setReviewing(null);
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Could not resolve: ${detail}`);
    },
  });

  const verify = useMutation({
    mutationFn: () => {
      if (!result?.approval_id) throw new Error('Nothing to verify');
      return verifyBudgetReallocation(result.approval_id);
    },
    onSuccess: (payload) => {
      const next: Record<string, BudgetVerifyRow> = {};
      for (const row of payload.rows) {
        next[row.campaign_id] = row;
      }
      setVerifyRows(next);
      const mismatches = payload.rows.filter((row) => row.status === 'mismatch').length;
      const unavailable = payload.rows.filter((row) => row.status === 'unavailable').length;
      if (unavailable) {
        toast.info(`${unavailable} row${unavailable === 1 ? '' : 's'} Unavailable — not assumed applied`);
      } else if (mismatches) {
        toast.error(`${mismatches} row${mismatches === 1 ? '' : 's'} Mismatch`);
      } else {
        toast.success('Verify — live Ads dailies match Proposed');
      }
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Verify failed: ${typeof detail === 'string' ? detail : 'refused'}`);
    },
  });

  const undo = useMutation({
    mutationFn: () => {
      if (!result?.approval_id) throw new Error('Nothing to undo');
      return undoBudgetReallocation(result.approval_id);
    },
    onSuccess: (payload) => {
      if (payload.partial) {
        const total = payload.target || result?.proposals.length || payload.undone;
        toast.info(
          `Reverted ${payload.undone} of ${total}. Retry to finish.`,
        );
        return;
      }
      toast.success(
        `Undo — restored ${payload.undone} Google Ads daily${
          payload.undone === 1 ? '' : 's'
        }. Save split was not updated.`,
      );
      setUndone(true);
    },
    onError: (err: unknown) => {
      const body = (err as { response?: { data?: { detail?: unknown } } })?.response
        ?.data?.detail;
      if (body && typeof body === 'object' && body !== null && 'reason' in body) {
        const reason = String((body as { reason?: string }).reason || 'refused');
        const rows = (body as { refused_rows?: string[] }).refused_rows || [];
        toast.error(
          rows.length
            ? `Undo refused (${reason}): ${rows.join(', ')}`
            : `Undo refused (${reason})`,
        );
        return;
      }
      toast.error(`Undo failed: ${String(body ?? err)}`);
    },
  });

  const busy = analyze.isPending || decide.isPending || verify.isPending || undo.isPending;
  const factsLive = result?.facts_source !== 'stub';
  const windowCurrent = isPreviousCalendarMonth(
    result?.period_start,
    result?.period_end,
  );
  const canDecide =
    Boolean(result?.approval_id) &&
    (result?.proposals.length ?? 0) > 0 &&
    factsLive &&
    windowCurrent;

  return (
    <section
      className={
        embedded
          ? ''
          : 'overflow-hidden rounded-md border border-border bg-warm-bg'
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          {embedded ? null : (
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Strategy
            </p>
          )}
          <h2 className={`${embedded ? '' : 'mt-1 '}flex items-center gap-2 text-base font-semibold`}>
            <Scale className="h-4 w-4 text-coral" />
            Budget moves
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Performance review. Yes writes the new Ads dailies. It does not
            change the pacing Save split. No leaves spend where it is.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          className="bg-coral hover:bg-coral-hover"
          disabled={!tenantId || busy}
          onClick={() => analyze.mutate()}
        >
          {analyze.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          {analyze.isPending ? 'Reviewing…' : 'Review strategy'}
        </Button>
      </div>

      <div className="px-4 py-3">
        {!result ? (
          <p className="text-sm text-muted-foreground">
            No open recommendation. Review strategy to fill this table.
          </p>
        ) : result.proposals.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {result.skipped_reason
              ? `Skipped: ${result.skipped_reason}`
              : result.summary || 'No campaign moves this period.'}
          </p>
        ) : (
          <div className="space-y-3">
            {result.summary ? (
              <p className="text-sm">{result.summary}</p>
            ) : null}
            {factsLive && !windowCurrent ? (
              <p className="text-sm text-muted-foreground">
                This package is not last calendar month. No, then Review
                strategy again. Yes stays off so a stale ranking cannot write
                Ads dailies.
              </p>
            ) : null}
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead className="text-right">Now</TableHead>
                    <TableHead className="text-right">Proposed</TableHead>
                    <TableHead className="text-right">Move</TableHead>
                    <TableHead>Why</TableHead>
                    <TableHead className="text-right">Evidence</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.proposals.map((proposal) => (
                    <ProposalRow
                      key={proposal.campaign_id}
                      proposal={proposal}
                      onReview={setReviewing}
                      factsLive={factsLive}
                      verifyStatus={verifyRows[proposal.campaign_id]?.status}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">
                  {result.period_start} → {result.period_end}
                </Badge>
                <Badge variant="outline">
                  Pulled {formatPulledAt(result.analyzed_at)}
                </Badge>
                {factsLive ? (
                  <Badge variant="outline">Live Ads facts</Badge>
                ) : (
                  <Badge variant="outline" className="border-destructive/40 text-destructive">
                    Stub Ads client — window facts are not live
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                {applied ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy || undone}
                      onClick={() => verify.mutate()}
                    >
                      {verify.isPending ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : null}
                      Verify
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-destructive/40 text-destructive hover:bg-destructive/10"
                      disabled={busy || undone}
                      onClick={() => undo.mutate()}
                    >
                      {undo.isPending ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <X className="mr-1 h-4 w-4" />
                      )}
                      Undo
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-destructive/40 text-destructive hover:bg-destructive/10"
                      disabled={!canDecide || busy}
                      onClick={() => decide.mutate('reject')}
                    >
                      <X className="mr-1 h-4 w-4" />
                      No
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="bg-[hsl(var(--status-success))] text-white hover:opacity-90"
                      disabled={!canDecide || busy}
                      onClick={() => decide.mutate('approve')}
                    >
                      {decide.isPending ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="mr-1 h-4 w-4" />
                      )}
                      Yes
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <BudgetStrategyReviewModal
        proposal={reviewing}
        periodStart={result?.period_start ?? ''}
        periodEnd={result?.period_end ?? ''}
        analyzedAt={result?.analyzed_at}
        onClose={() => setReviewing(null)}
      />
    </section>
  );
}
